/**
 * Utility functions for handling dynamic imports safely
 * Helps prevent ChunkLoadError issues
 */

import React from 'react';

/**
 * Creates a safe dynamic import with retry logic
 * @param importFn - The import function
 * @param retries - Number of retries (default: 3)
 * @param delay - Delay between retries in ms (default: 1000)
 */
export function createSafeDynamicImport<T = any>(
  importFn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): () => Promise<T> {
  return async () => {
    let lastError: Error;
    
    for (let i = 0; i <= retries; i++) {
      try {
        return await importFn();
      } catch (error: any) {
        lastError = error;
        
        // If it's a chunk load error and we have retries left, wait and try again
        if (
          (error.name === 'ChunkLoadError' || 
           error.message?.includes('Loading chunk') ||
           error.message?.includes('Loading CSS chunk')) &&
          i < retries
        ) {
          console.warn(`Chunk load failed, retrying in ${delay}ms... (attempt ${i + 1}/${retries + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // If it's not a chunk error or we're out of retries, throw
        throw error;
      }
    }
    
    throw lastError!;
  };
}

/**
 * Creates a React lazy component with safe dynamic import
 * @param importFn - The import function
 * @param retries - Number of retries (default: 3)
 */
export function createSafeLazyComponent<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries: number = 3
): React.LazyExoticComponent<T> {
  const safeImport = createSafeDynamicImport(importFn, retries);
  return React.lazy(safeImport);
}

/**
 * Error boundary component specifically for handling chunk load errors
 */
interface ChunkErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ChunkErrorBoundary extends React.Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);
    
    // If it's a chunk load error, we might want to reload the page
    if (
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk')
    ) {
      console.warn('Chunk load error detected in error boundary');
    }
  }

  retry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;
      
      if (Fallback) {
        return <Fallback error={this.state.error!} retry={this.retry} />;
      }
      
      // Default fallback for chunk errors
      if (
        this.state.error?.name === 'ChunkLoadError' ||
        this.state.error?.message?.includes('Loading chunk')
      ) {
        return (
          <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              Loading Error
            </h3>
            <p className="text-yellow-700 mb-3">
              There was an issue loading this component. This might be due to a network issue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              Reload Page
            </button>
          </div>
        );
      }
      
      // Generic error fallback
      return (
        <div className="p-4 border border-red-300 bg-red-50 rounded-md">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Something went wrong
          </h3>
          <p className="text-red-700 mb-3">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.retry}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
